import type { ReactElement, ReactNode } from 'react';
import type { BasicPermission } from '@backstage/plugin-permission-common';
import { RequirePermission } from '@backstage/plugin-permission-react';

import { KsdpAccessDenied } from './KsdpAccessDenied';

export { KsdpAccessDenied };

export interface RequireKsdpPermissionProps {
  permission: BasicPermission;
  children: ReactNode;
}

/**
 * Renders `children` only when the user holds `permission`, otherwise the
 * KSDP access-denied page. Renders nothing while the decision loads.
 *
 * UI-only: it hides what a user cannot use. The backend routers enforce the
 * same permissions on every route, so this is never the security boundary.
 */
export function RequireKsdpPermission({
  permission,
  children,
}: RequireKsdpPermissionProps) {
  return (
    <RequirePermission permission={permission} errorPage={<KsdpAccessDenied />}>
      {children}
    </RequirePermission>
  );
}

/**
 * `PageBlueprint` loader helper: wraps a page element in
 * {@link RequireKsdpPermission}.
 *
 * @example
 * loader: async () => guardKsdpPage(ksdpRiskReadPermission, <RiskLibraryPage />)
 */
export function guardKsdpPage(
  permission: BasicPermission,
  page: ReactElement,
): ReactElement {
  return (
    <RequireKsdpPermission permission={permission}>
      {page}
    </RequireKsdpPermission>
  );
}
